import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { ConfirmModal } from '../ui/ConfirmModal';
import { getEvents, createEvent, updateEvent, deleteEvent } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Calendar, MapPin, ExternalLink } from 'lucide-react';

export const EventsManager = () => {
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, event: null });
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    time: '',
    venue: '',
    city: '',
    description: '',
    image: '',
    lineup: '',
    ticketUrl: '',
    facebookUrl: ''
  });

  const fetchEvents = useCallback(async () => {
    try {
      const response = await getEvents();
      setEvents(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch events', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await updateEvent(editingEvent.id, formData);
        toast({ title: 'Success', description: 'Event updated successfully' });
      } else {
        await createEvent(formData);
        toast({ title: 'Success', description: 'Event created successfully' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save event', variant: 'destructive' });
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      name: event.name || '',
      date: event.date || '',
      time: event.time || '',
      venue: event.venue || '',
      city: event.city || '',
      description: event.description || '',
      image: event.image || '',
      lineup: event.lineup || '',
      ticketUrl: event.ticketUrl || '',
      facebookUrl: event.facebookUrl || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteEvent(deleteModal.event.id);
      toast({ title: 'Success', description: 'Event deleted successfully' });
      fetchEvents();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete event', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      date: '', 
      time: '',
      venue: '', 
      city: '', 
      description: '',
      image: '',
      lineup: '',
      ticketUrl: '',
      facebookUrl: ''
    });
    setEditingEvent(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400">Events</h1>
          <p className="text-gray-400 mt-2">Manage upcoming events</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600">
              <Plus className="w-4 h-4 mr-2" /> Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-black border-orange-500/30 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{editingEvent ? 'Edit Event' : 'Add New Event'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm font-medium mb-2">Event Name *</label>
                <Input name="name" value={formData.name} onChange={handleChange} required className="bg-black/50 border-green-500/30" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Date *</label>
                  <Input name="date" type="date" value={formData.date} onChange={handleChange} required className="bg-black/50 border-green-500/30" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Time (e.g. 22:00)</label>
                  <Input name="time" value={formData.time} onChange={handleChange} placeholder="22:00" className="bg-black/50 border-green-500/30" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Venue *</label>
                  <Input name="venue" value={formData.venue} onChange={handleChange} required className="bg-black/50 border-green-500/30" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">City *</label>
                  <Input name="city" value={formData.city} onChange={handleChange} required className="bg-black/50 border-green-500/30" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea name="description" value={formData.description} onChange={handleChange} rows={4} placeholder="Event description..." className="bg-black/50 border-green-500/30" />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Lineup (Artists)</label>
                <Textarea name="lineup" value={formData.lineup} onChange={handleChange} rows={3} placeholder="Artist 1\nArtist 2\nArtist 3" className="bg-black/50 border-green-500/30" />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Event Image URL</label>
                <Input name="image" value={formData.image} onChange={handleChange} placeholder="https://..." className="bg-black/50 border-green-500/30" />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Ticket URL *</label>
                <Input name="ticketUrl" value={formData.ticketUrl} onChange={handleChange} required placeholder="https://..." className="bg-black/50 border-green-500/30" />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Facebook Event URL</label>
                <Input name="facebookUrl" value={formData.facebookUrl} onChange={handleChange} placeholder="https://facebook.com/events/..." className="bg-black/50 border-green-500/30" />
              </div>
              
              <Button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600">
                {editingEvent ? 'Update Event' : 'Create Event'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {events.map((event) => (
          <Card key={event.id} className="bg-black/50 border-green-500/30 p-6 hover:border-green-500 transition-colors">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-3">{event.name}</h3>
                <div className="space-y-2">
                  <div className="flex items-center text-gray-400">
                    <Calendar className="w-5 h-5 mr-3 text-orange-400" />
                    <span>{new Date(event.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center text-gray-400">
                    <MapPin className="w-5 h-5 mr-3 text-green-400" />
                    <span>{event.venue}, {event.city}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(event)} className="border-green-500/30 hover:bg-green-500/20">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDeleteModal({ isOpen: true, event })} className="border-red-500/30 hover:bg-red-500/20 text-red-400">
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(event.ticketUrl, '_blank')} className="border-orange-500/30 hover:bg-orange-500/20">
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, event: null })}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete the event "${deleteModal.event?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};